import {
    Card, Title, Box, Text, ScrollArea, Stack, Group, Divider,
    Badge, Button
} from '@mantine/core';
import { IconTruck } from '@tabler/icons-react';
import { PackageToShip } from '../../types/shipping';

interface PackageSummaryProps {
    packages: PackageToShip[];
    packageId: string | null;
    isProcessing: boolean;
}

const PackageSummary = ({
    packages,
    packageId,
    isProcessing
}: PackageSummaryProps) => {

    const calculateTotalWeight = () => {
        return packages.reduce((total, pkg) => {
            const weight = typeof pkg.weight === 'string' ? parseFloat(pkg.weight) : pkg.weight;
            return total + (Number.isNaN(weight) ? 0 : weight);
        }, 0);
    };

    const calculateTotalValue = () => {
        return packages.reduce((total, pkg) => {
            const value = typeof pkg.value === 'string' ? parseFloat(pkg.value) : pkg.value;
            return total + (Number.isNaN(value) ? 0 : value);
        }, 0);
    };



    const calculateTotalCost = () => {
        const shippingCost = packages.reduce((total, pkg) => {
            return total + (pkg.shipping_cost || 0);
        }, 0);

        // Uncomment if we want to include insurance in total
        // const insuranceCost = calculateInsuranceCost();
        return shippingCost;
    };

    return (
        <Card withBorder shadow="sm" radius="md" p="lg">
            <Title order={2} size="h3" mb="md">Package Summary</Title>

            {/* Packages List */}
            <Box mb="md">
                {packageId && <Text fw={700} mb="xs">#{packageId}</Text>}
                <ScrollArea h={packages.length > 2 ? 150 : undefined} mb="md">
                    <Stack>
                        {packages.map(pkg => (
                            <Box key={pkg.id}>
                                <Text size="sm">{pkg.name}</Text>
                                <Group mt={4}>
                                    <Text size="xs" c="dimmed">Weight: {pkg.weight} kg</Text>
                                    <Text size="xs" c="dimmed">Value: ${pkg.value.toFixed(2)}</Text>
                                </Group>
                            </Box>
                        ))}
                    </Stack>
                </ScrollArea>

                <Divider my="sm" />

                <Group>
                    <Text size="sm">Total Weight:</Text>
                    <Text size="sm">{calculateTotalWeight().toFixed(1)} kg</Text>
                </Group>

                <Group>
                    <Text size="sm">Total Value:</Text>
                    <Text size="sm">${calculateTotalValue().toFixed(2)}</Text>
                </Group>
            </Box>

            <Divider my="md" />

            {/* Cost Summary */}
            <Box mb="lg">
                <Text fw={700} mb="xs">Cost Summary</Text>

                <Group mb="xs">
                    <Text size="sm">Shipping:</Text>
                    <Text size="sm">${packages.reduce((total, pkg) => total + (pkg.shipping_cost || 0), 0).toFixed(2)}</Text>
                </Group>



                <Divider my="sm" />

                <Group>
                    <Text fw={700}>Total:</Text>
                    <Badge size="lg" variant="filled" p="md">
                        ${calculateTotalCost().toFixed(2)}
                    </Badge>
                </Group>
            </Box>

            <Button
                type="submit"
                leftSection={<IconTruck size={18} />}
                size="lg"
                fullWidth
                loading={isProcessing}
            >
                {isProcessing ? 'Shipping...' : 'Ship Package'}
            </Button>

            <Text size="xs" c="dimmed" ta="center" mt="sm">
                By confirming your order, you agree to our Terms of Service and Privacy Policy.
            </Text>
        </Card>
    );
};

export default PackageSummary;
